import { useState } from "react";
import { KeyRound, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


export default function KeysPanel({ appId, keys, onNewKey, onChanged }) {
    const [scope, setScope] = useState("publish");
    const [busy, setBusy] = useState(false);

    const create = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/keys`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ label: `${scope} key`, scope }),
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            onNewKey?.(d.key);
            onChanged?.();
        } catch {
            toast.error("Could not create key");
        } finally {
            setBusy(false);
        }
    };

    const rotate = async (keyId) => {
        try {
            const r = await fetch(`${API}/apps/${appId}/keys/${keyId}/rotate`, {
                method: "POST",
                credentials: "include",
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            onNewKey?.(d.key);
            onChanged?.();
            toast.success("Key rotated", { description: "The old key was revoked." });
        } catch {
            toast.error("Rotation failed");
        }
    };

    const revoke = async (keyId) => {
        try {
            await fetch(`${API}/apps/${appId}/keys/${keyId}`, {
                method: "DELETE",
                credentials: "include",
            });
            onChanged?.();
            toast.success("Key revoked");
        } catch {
            toast.error("Revoke failed");
        }
    };

    return (
        <div data-testid="keys-panel" className="glass-panel mt-4 rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <KeyRound className="h-4 w-4 text-cyan-400" />
                    API keys
                </h3>
                <div className="flex items-center gap-2">
                    <select
                        data-testid="key-scope-select"
                        value={scope}
                        onChange={(e) => setScope(e.target.value)}
                        className="rounded-md border border-slate-800 bg-slate-950/70 px-2.5 py-1.5 font-mono text-[11px] text-slate-300 focus:border-cyan-500/60 focus:outline-none"
                    >
                        <option value="read">read</option>
                        <option value="publish">publish</option>
                        <option value="admin">admin</option>
                    </select>
                    <button
                        data-testid="create-key-button"
                        onClick={create}
                        disabled={busy}
                        className="rounded-md border border-cyan-500/40 bg-cyan-950/40 px-3.5 py-1.5 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70 disabled:opacity-50"
                    >
                        New key
                    </button>
                </div>
            </div>
            <div className="mt-4 space-y-2">
                {(keys || []).map((k) => (
                    <div
                        key={k.key_id}
                        data-testid={`key-row-${k.key_id}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800/80 bg-slate-950/50 px-4 py-2.5 font-mono text-[11px]"
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-slate-300">{k.prefix}</span>
                            <span className="rounded border border-slate-700 px-1.5 py-px text-[9px] uppercase tracking-wider text-slate-400">
                                {k.scope}
                            </span>
                            <span className="hidden text-slate-600 sm:inline">{k.label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-slate-600">
                                {(k.created_at || "").slice(0, 10)}
                            </span>
                            {k.revoked ? (
                                <span className="text-rose-400">revoked</span>
                            ) : (
                                <>
                                    <button
                                        data-testid={`key-rotate-${k.key_id}`}
                                        onClick={() => rotate(k.key_id)}
                                        className="flex items-center gap-1 text-slate-500 transition-colors hover:text-cyan-300"
                                    >
                                        <RefreshCw className="h-3 w-3" /> rotate
                                    </button>
                                    <button
                                        data-testid={`key-revoke-${k.key_id}`}
                                        onClick={() => revoke(k.key_id)}
                                        className="flex items-center gap-1 text-slate-500 transition-colors hover:text-rose-300"
                                    >
                                        <Trash2 className="h-3 w-3" /> revoke
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
                {(keys || []).length === 0 && (
                    <p className="rounded-lg border border-dashed border-slate-800 py-6 text-center font-mono text-[11px] text-slate-600">
                        No keys yet — create one to start publishing.
                    </p>
                )}
            </div>
        </div>
    );
}
